import React, { useState, useEffect, useRef, createContext } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import HomePage from './components/HomePage';
import PortfolioPage from './components/PortfolioPage';
import CardPage from './components/CardPage';
import BankTransferPage from './components/BankTransferPage';
import QrPaymentPage from './components/QrPaymentPage';
import PromoCard from './components/PromoCard';
import PartnerPage from './components/PartnersPage';
import WithdrawPage from './components/WithdrawPage';
import PayeerTransferPage from './components/PayeerTransferPage';
import './styles.css';
import axios from './axiosInstance';
import logo from "./assets/logo.png";
import btc from './assets/btc.png';
import usdt from './assets/usdt.png';
import ton from './assets/ton.png';
import eth from './assets/eth.png';
import bnb from './assets/bnb.png';
import { useImagesLoaded } from "./hooks/useImagesLoaded";
import BanScreen from "./BanScreen";
import SubscriptionGate from './components/SubscriptionGate';
import AvatarOverlay from './components/AvatarOverlay';
import './pages/AvatarOverlay.css';
import FocusViewportManager from "./components/FocusViewportManager";
import CryptoDepositPage from "./components/CryptoDepositPage";


export const ConfigContext = createContext(null);

if (process.env.NODE_ENV === "production") {
  window.onerror = () => true;
  window.onunhandledrejection = () => true;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const routesMap = {
    home: '/',
    portfolio: '/portfolio',
    card: '/card',
    news: '/news',
    partners: '/partners'
  };

  const [config, setConfig] = useState(null);
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [errorAlertText, setErrorAlertText] = useState('');
  const [successAlertText, setSuccessAlertText] = useState('');
  const [isRateBanned, setIsRateBanned] = useState(false);
  const [banSecondsLeft, setBanSecondsLeft] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);
  const imageUrls = [logo, btc, usdt, ton, eth, bnb];
  const imagesLoaded = useImagesLoaded(imageUrls);

  useEffect(() => {
    const tg = window.Telegram && window.Telegram.WebApp;
    try { tg?.ready?.(); tg?.requestFullscreen?.(); tg?.expand?.(); } catch {}
    const reapply = () => { if (document.visibilityState === 'visible') { try { tg?.requestFullscreen?.(); } catch {} } };
    const onFocus = () => { try { tg?.requestFullscreen?.(); } catch {} };
    document.addEventListener('visibilitychange', reapply);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', reapply);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const updateViewportVars = () => {
      const vv = window.visualViewport;
      let kbBottom = 0;
      if (vv) {
        const overlap = window.innerHeight - (vv.height + (vv.offsetTop || 0));
        kbBottom = Math.max(0, Math.round(overlap));
      }
      root.style.setProperty('--kb-bottom', `${kbBottom}px`);
      if (kbBottom > 0) document.body.classList.add('kb-open'); else document.body.classList.remove('kb-open');
    };
    const ensureFocusVisible = () => {
      const active = document.activeElement;
      if (!active) return;
      const dialog = active.closest(':where(.ReactModal__Content,[data-radix-dialog-content],[data-reach-dialog-content],[role="dialog"] .DialogContent,[class*="ModalContent"],[class*="dialog-content"],[class*="modal-content"])');
      const scroller = dialog || document.querySelector('.scroll-area');
      if (scroller) {
        setTimeout(() => active.scrollIntoView({ block: 'nearest', inline: 'nearest' }), 50);
      }
    };
    updateViewportVars();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', updateViewportVars);
      vv.addEventListener('scroll', updateViewportVars);
    }
    window.addEventListener('orientationchange', updateViewportVars);
    window.addEventListener('resize', updateViewportVars);
    const onFocusIn = () => { updateViewportVars(); ensureFocusVisible(); };
    const onFocusOut = () => { updateViewportVars(); };
    window.addEventListener('focusin', onFocusIn);
    window.addEventListener('focusout', onFocusOut);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateViewportVars);
        vv.removeEventListener('scroll', updateViewportVars);
      }
      window.removeEventListener('orientationchange', updateViewportVars);
      window.removeEventListener('resize', updateViewportVars);
      window.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  useEffect(() => {
    const sel = [
      '.ReactModal__Overlay',
      '[data-radix-dialog-overlay]',
      '[data-reach-dialog-overlay]',
      '[role="dialog"].overlay',
      '[class*="ModalOverlay"]',
      '[class*="DialogOverlay"]',
      '[class*="modal-overlay"]',
      '.ReactModal__Content',
      '[data-radix-dialog-content]',
      '[data-reach-dialog-content]',
      '[role="dialog"] .DialogContent',
      '[class*="ModalContent"]',
      '[class*="dialog-content"]',
      '[class*="modal-content"]',
    ].join(',');
    const updateFlag = () => {
      const open = document.querySelector(sel);
      document.body.classList.toggle('dialog-open', !!open);
    };
    const mo = new MutationObserver(updateFlag);
    mo.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'open', 'aria-hidden', 'aria-modal'] });
    updateFlag();
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) document.documentElement.classList.add('ios-zoom');
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") navigate("/", { replace: true });
  }, []);

  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const checkRateBan = async () => {
    try {
      const res = await axios.get("/rate-limit/status", { withCredentials: true });
      if (res.data.banned || res.data.is_banned) {
        setIsRateBanned(true);
        setBanSecondsLeft(res.data.seconds_left || res.data.ban_seconds_left || 300);
        window.__RATE_BANNED__ = true;
      } else {
        setIsRateBanned(false);
        window.__RATE_BANNED__ = false;
      }
    } catch {}
  };

  useEffect(() => {
    if (isRateBanned && banSecondsLeft <= 0) window.location.reload();
    if (!isRateBanned || !banSecondsLeft) return;
    const t = setTimeout(() => setBanSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [isRateBanned, banSecondsLeft]);

  const refreshUserData = async () => {
    try {
      const [meRes, trxRes] = await Promise.all([
        axios.get("/me/", { withCredentials: true }),
        axios.get("/transactions/recent", { withCredentials: true }),
      ]);
      setUserDetails(meRes.data);
      setTransactions(trxRes.data);
    } catch {}
  };

  const fetchConfig = async () => {
    try {
      setConfigLoading(true);
      const res = await axios.get(`/config/?_=${Date.now()}`, { withCredentials: true });
      setConfig(res.data);
    } catch {}
    setConfigLoading(false);
  };

  useEffect(() => {
    if (!user?.telegram_id || !window.Telegram || isRateBanned) return;
    let isCurrent = true;
    let reconnectAttempts = 0;
    const MAX_RECONNECTS = 5;
    const cleanupWS = () => {
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
    const connect = () => {
      cleanupWS();
      if (!isCurrent) return;
      const ws = new WebSocket(`wss://api.mrtradeweb.com/ws/`);
      wsRef.current = ws;
      ws.onopen = () => {
        if (!isCurrent) return;
        reconnectAttempts = 0;
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN && isCurrent) connect();
        }, 10000);
        refreshUserData();
      };
      ws.onmessage = (event) => {
        if (!isCurrent) return;
        try {
          const data = JSON.parse(event.data);
          const currentUser = userRef.current;
          if (data.type === "balance_update" && currentUser) {
            setUser(prev => ({ ...prev, deposit: data.new_deposit, balance: data.new_balance }));
            setUserDetails(prev => {
              const isValidAmount = typeof data.amount === "number" && Number.isFinite(data.amount) && data.amount !== 0;
              if (!isValidAmount) {
                return { ...prev, deposit: data.new_deposit, balance: data.new_balance };
              }
              const newEntry = {
                id: data.tx_id || 0,
                type: data.transaction_type || "deposit",
                amount: data.amount,
                status: "Успешно",
                created_at: data.created_at || data.timestamp
              };
              const combined = [newEntry, ...(prev?.investments || [])].slice(0, 10);
              return { ...prev, deposit: data.new_deposit, balance: data.new_balance, investments: combined };
            });
          }
          if (data.type === "config_update") fetchConfig();
          if (data.type === "withdrawal_update" && currentUser) {
            setUserDetails(prev => {
              const exists = (prev?.withdrawal_requests || []).some(w => w.id === data.tx_id);
              const updatedRequests = exists
                ? prev.withdrawal_requests.map(w =>
                    w.id === data.tx_id ? { ...w, status: data.status, amount: data.amount, created_at: data.created_at } : w
                  )
                : [{ id: data.tx_id, type: "withdraw", amount: data.amount, status: data.status, created_at: data.created_at }, ...(prev?.withdrawal_requests || [])];
              return { ...prev, withdrawal_requests: updatedRequests.slice(0, 10), balance: data.new_balance };
            });
          }
          if (data.type === "savings_update") setUserDetails(prev => ({ ...prev, savings: data.new_savings }));
          if (data.type === "partner_earnings_update") setUserDetails(prev => ({ ...prev, partner_earnings: data.new_partner_earnings, balance: data.new_balance }));
        } catch {}
      };
      ws.onclose = () => {
        if (!isCurrent) return;
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (reconnectAttempts < MAX_RECONNECTS) {
          reconnectAttempts++;
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => {
        if (!isCurrent) return;
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        ws.close();
      };
    };
    const handleOnline = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) connect();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) connect();
        else refreshUserData();
      }
    };
    connect();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      isCurrent = false;
      cleanupWS();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [user?.telegram_id, isRateBanned]);

  const waitForInitData = async () => {
    const tg = window.Telegram && window.Telegram.WebApp;
    while (!tg || !tg.initData || tg.initData === "") {
      await new Promise(r => setTimeout(r, 250));
    }
    return tg && tg.initData && tg.initData !== "" ? tg.initData : null;
  };

  const recheckSubscription = async () => {
    if (!userRef.current?.telegram_id) return;
    try {
      const res = await axios.get("/subscription/check", { params: { user_id: userRef.current.telegram_id }, withCredentials: true });
      setSubscription(res.data);
      if (res.data?.success) setShowSubscriptionGate(false);
      return res.data;
    } catch {}
  };

  useEffect(() => {
    let isMounted = true;
    const registerUser = async () => {
      try {
        const initData = await waitForInitData();
        if (!initData) return;
        const initParams = new URLSearchParams(initData);
        if (initParams.get("start_param") === "tttsss") {
          navigate("/bonus-promo", { replace: true });
        }
        const referrer_id = initParams.get("start_param");
        const response = await axios.post("/webapp/", { initData, referrer_id }, { headers: { "Content-Type": "application/json" }, withCredentials: true });
        if (!isMounted) return;
        const userData = {
          telegram_id: response.data.telegram_id,
          balance: response.data.balance,
          deposit: response.data.deposit,
          referrer_id: response.data.referrer_id,
          first_name: response.data.first_name,
          last_name: response.data.last_name,
          username: response.data.username,
          photo_url: response.data.photo_url,
        };
        setUser(userData);
        if (response.data.subscription) {
          setSubscription(response.data.subscription);
          setShowSubscriptionGate(!response.data.subscription.success);
        } else {
          const checked = await recheckSubscription();
          setShowSubscriptionGate(prev => prev || !(checked?.success || subscription?.success));
        }
        await checkRateBan();
        await fetchConfig();
        const [meRes, trxRes] = await Promise.all([
          axios.get("/me/", { withCredentials: true }),
          axios.get("/transactions/recent", { withCredentials: true }),
        ]);
        if (isMounted) {
          setUserDetails(meRes.data);
          setTransactions(trxRes.data);
          setLoading(false);
        }
      } catch {}
    };
    registerUser();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!user?.telegram_id) return;
    const controller = new AbortController();
    const fetchTransactions = async () => {
      try {
        const res = await axios.get('/transactions/recent', { signal: controller.signal, withCredentials: true });
        setTransactions(res.data);
      } catch {}
    };
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchTransactions();
    }, 60000);
    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [user?.telegram_id]);

  const [isCollecting, setIsCollecting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  const handleCollectSavings = async () => {
    if (isCollecting) return;
    setIsCollecting(true);
    try {
      const res = await axios.post("/collect-savings", {}, { withCredentials: true });
      const success = res?.data?.success;
      const message = res?.data?.message;
      if (!success) {
        setErrorAlertText(message || "Не удалось собрать накопления.");
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 2500);
        return;
      }
      setUserDetails(prev => ({ ...prev, savings: 0.00 }));
      setSuccessAlertText("Накопления успешно собраны!");
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 2500);
    } catch (error) {
      const detail = error?.response?.data?.detail || "Не удалось собрать накопления. Попробуйте позже.";
      setErrorAlertText(detail);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 2500);
    } finally {
      setIsCollecting(false);
    }
  };

  const reinvestCooldown = useRef(false);
  const handleReinvest = async (amount) => {
    if (reinvestCooldown.current) return;
    reinvestCooldown.current = true;
    setTimeout(() => (reinvestCooldown.current = false), 2000);
    const cleanAmount = parseFloat(String(amount).replace(',', '.'));
    if (isNaN(cleanAmount) || cleanAmount < 10) {
      setErrorAlertText("Введите корректную сумму (от 10₽).");
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 2500);
      return;
    }
    try {
      const response = await axios.post("/users/reinvest", { amount: cleanAmount, percent: 4 }, { withCredentials: true });
      if (response.data && response.data.success !== false) {
        setSuccessAlertText("Реинвест успешно выполнен!");
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 2500);
        refreshUserData();
      } else {
        const detail = response.data?.detail || "Не удалось выполнить реинвест.";
        setErrorAlertText(detail);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 2500);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail || "Не удалось выполнить реинвест. Попробуйте позже.";
      setErrorAlertText(detail);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 2500);
    }
  };

  useEffect(() => {
    const tg = window.Telegram && window.Telegram.WebApp;
    if (!tg) return;
    const showBack = location.pathname !== '/';
    if (showBack) tg.BackButton.show(); else tg.BackButton.hide();
    const handleBack = () => {
      if (location.pathname !== '/') {
        if (window.history.length > 1) navigate(-1);
        else navigate('/', { replace: true });
      }
    };
    tg.onEvent('backButtonClicked', handleBack);
    return () => {
      tg.offEvent('backButtonClicked', handleBack);
    };
  }, [location.pathname, navigate]);

  if (isRateBanned) {
    return (
      <BanScreen
        secondsLeft={banSecondsLeft}
        onClose={() => {
          if (window.Telegram?.WebApp?.close) window.Telegram.WebApp.close();
          else window.location.href = "/";
        }}
      />
    );
  }

  if (loading || configLoading || !imagesLoaded || !user || !userDetails) {
    return (
      <div className="loader-center">
        <div className="loader-wrapper">
          <div className="loader-logo-wrapper">
            <img className="loader-logo" src={logo} alt="Logo" />
          </div>
          <div className="glow-circle" />
          <div className="loading-text">
            Загрузка <span className="dot-animate"><span>.</span><span>.</span><span>.</span></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <FocusViewportManager />
      <div className="app-container">
        <div className="page-center">
          <div className="page-inner">
            <div className="scroll-area">
              <ConfigContext.Provider value={config}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <HomePage
                        user={user}
                        userDetails={userDetails}
                        transactions={transactions}
                        btc={btc}
                        usdt={usdt}
                        ton={ton}
                        eth={eth}
                      />
                    }
                  />
                  <Route
                    path="/portfolio"
                    element={
                      <PortfolioPage
                        user={user}
                        userDetails={userDetails}
                        isCollecting={isCollecting}
                        showSuccessAlert={showSuccessAlert}
                        showErrorAlert={showErrorAlert}
                        setShowErrorAlert={setShowErrorAlert}
                        handleCollectSavings={handleCollectSavings}
                        handleReinvest={handleReinvest}
                        btc={btc}
                        usdt={usdt}
                        ton={ton}
                        eth={eth}
                      />
                    }
                  />
                  <Route path="/card" element={<CardPage user={user} userDetails={userDetails} btc={btc} usdt={usdt} ton={ton} eth={eth} bnb={bnb} logo={logo} />} />
                  <Route path="/bank-transfer" element={<BankTransferPage user={user} />} />
				  <Route path="/crypto-deposit" element={<CryptoDepositPage user={user} logo={logo} />} />
                  <Route path="/qr-transfer" element={<QrPaymentPage user={user} />} />
                  <Route path="/payeer" element={<PayeerTransferPage user={user} />} />
                  <Route path="/withdraw" element={<WithdrawPage />} />
                  <Route path="/news" element={<PromoCard user={user} btc={btc} usdt={usdt} ton={ton} eth={eth} />} />
                  <Route path="/partners" element={<PartnerPage user={user} userDetails={userDetails} btc={btc} usdt={usdt} ton={ton} eth={eth} />} />
                </Routes>
              </ConfigContext.Provider>
            </div>
          </div>
        </div>
      </div>

      <AvatarOverlay user={user} userDetails={userDetails} />

      <SubscriptionGate
        open={showSubscriptionGate}
        subscription={subscription}
        onRecheck={recheckSubscription}
        onClose={() => setShowSubscriptionGate(false)}
      />

      {showErrorAlert && <div className="error-alert modal-guard">{errorAlertText}</div>}
      {showSuccessAlert && <div className="success-alert modal-guard">{successAlertText}</div>}

      <footer className="bottom-nav">
        <div className={`nav-item ${location.pathname === '/' ? 'active' : ''}`} onClick={() => navigate(routesMap.home)}>
          <svg width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.7498 8.70977L14.4168 4.56177C13.9488 4.19768 13.3727 4 12.7798 4C12.1868 4 11.6108 4.19768 11.1428 4.56177L5.80879 8.70977C5.48823 8.95905 5.22888 9.2783 5.05054 9.64312C4.8722 10.0079 4.77959 10.4087 4.77979 10.8148V18.0148C4.77979 18.5452 4.9905 19.0539 5.36557 19.429C5.74064 19.8041 6.24935 20.0148 6.77979 20.0148H18.7798C19.3102 20.0148 19.8189 19.8041 20.194 19.429C20.5691 19.0539 20.7798 18.5452 20.7798 18.0148V10.8148C20.7798 9.99177 20.3998 9.21477 19.7498 8.70977Z" stroke="#6366F1" strokeWidth="1.5"/>
          </svg>
          <span>Главная</span>
        </div>

        <div className={`nav-item ${location.pathname === '/portfolio' ? 'active' : ''}`} onClick={() => navigate(routesMap.portfolio)}>
          <svg width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.25 11L12.25 14L20.25 6M20.25 12V18C20.25 18.5304 20.0393 19.0391 19.6642 19.4142C19.2891 19.7893 18.7804 20 18.25 20H6.25C5.71957 20 5.21086 19.7893 4.83579 19.4142C4.46071 19.0391 4.25 18.5304 4.25 18V6C4.25 5.46957 4.46071 4.96086 4.83579 4.58579C5.21086 4.21071 5.71957 4 6.25 4H15.25" stroke={location.pathname === '/portfolio' ? '#6950E8' : '#64748B'} strokeWidth="1.5"/>
          </svg>
          <span>Портфель</span>
        </div>

        <div className="nav-item placeholder"></div>

        <div className={`nav-item ${location.pathname === '/news' ? 'active' : ''}`} onClick={() => navigate(routesMap.news)}>
          <svg width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.75 9H16.75M8.75 13H14.75M12.75 20L9.75 17H7.75C6.95435 17 6.19129 16.6839 5.62868 16.1213C5.06607 15.5587 4.75 14.7956 4.75 14V8C4.75 7.20435 5.06607 6.44129 5.62868 5.87868C6.19129 5.31607 6.95435 5 7.75 5H17.75C18.5456 5 19.3087 5.31607 19.8713 5.87868C20.4339 6.44129 20.75 7.20435 20.75 8V14C20.75 14.7956 20.4339 15.5587 19.8713 16.1213C19.3087 16.6839 18.5456 17 17.75 17H15.75L12.75 20Z" stroke="#64748B" strokeWidth="1.5"/>
          </svg>
          <span>Новости</span>
        </div>

        <div className={`nav-item ${location.pathname === '/partners' ? 'active' : ''}`} onClick={() => navigate(routesMap.partners)}>
          <svg width="25" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.25 11C14.4591 11 16.25 9.20914 16.25 7C16.25 4.79086 14.4591 3 12.25 3C10.0409 3 8.25 4.79086 8.25 7C8.25 9.20914 10.0409 11 12.25 11Z" stroke="#64748B" strokeWidth="1.5" />
            <path d="M6.25 21V19C6.25 17.9391 6.67143 16.9217 7.42157 16.1716C8.17172 15.4214 9.18913 15 10.25 15H14.25C15.3109 15 16.3283 15.4214 17.0784 16.1716C17.8286 16.9217 18.25 17.9391 18.25 19V21" stroke="#64748B" strokeWidth="1.5" />
          </svg>
          <span>Партнёры</span>
        </div>

        <div className="central-fab" onClick={() => navigate(routesMap.card)} style={{ cursor: 'pointer' }}>
          <div className="nav-button">
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <mask
                id="mask0_1_366"
                style={{ maskType: 'alpha' }}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="24"
                height="25"
              >
                <rect
                  y="0.5"
                  width="24"
                  height="24"
                  fill="url(#pattern0_1_366)"
                />
              </mask>
              <g mask="url(#mask0_1_366)">
                <rect
                  y="0.5"
                  width="24"
                  height="24"
                  fill={location.pathname === '/card' ? '#ffffff' : '#111827'}
                />
              </g>
              <defs>
                <pattern
                  id="pattern0_1_366"
                  patternContentUnits="objectBoundingBox"
                  width="1"
                  height="1"
                >
                  <use xlinkHref="#image0_1_366" transform="scale(0.00195312)" />
                </pattern>
                <image
                  id="image0_1_366"
                  width="512"
                  height="512"
                  preserveAspectRatio="none"
                  xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAgAAAAIACAMAAADDpiTIAAAAA3NCSVQICAjb4U/gAAAACXBIWXMAABKlAAASpQGfJySsAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAWtQTFRF////AQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQACAQAClZq7wgAAAHh0Uk5TAAECAwUGCAkLDg8QERITFhgZGxwdHiEjJygrLDA0NTc4OTw9PkJISk1OUFNUWVxdX2Rsbm9xdXl+gYKEh4iKi4yOj5aYm52eoKGprK2ur7Cys7i5vr/CxcbIycrP0dXa29zf4OLk5ufp6uvu7/Dx9PX29/j6+/z9SCQMpwAABhJJREFUeNrt3XlXlVUYh+GHQSExEaUMw1CTyBKCTIRCVCpIEjVNkCiRQlFskCNgH78/nEBBmdYJ3ue6PsJv32sxHfaOAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADKpqK+ua2r5xzbXk9XW3N9xRYefdWJ/pHx6fl/2UHmp8dH+j+u3Pzp15wcmjXnTvVg8NPdmzn9fZ1XHllxZ/vn8hd7N3j8jZcW7VcEC4MHNnD8e/pKpiuKuW/eWefxV5/2lb9QHnxZvZ7zP3XHZEUz+fnav/iPmauIxhrXdv4tM7YqppmWtZx/u2/+CqvU/vbf+Paaqch63/I74pphGxXbcM2bzr9hwkJFN9Gw+vnXTdqn+CbrVv2z3w3rZHCjapUALtomh4srn3+nZbLoXOn8jz42TBaPj75+/gfu2SWPe6/9gXi3HwBTmXj1k0JnbZLL2eXn/+5fJsnlz+UfFLtgkWz6l57/e34CSKe09PvAH+yRz/cvz//DJ+bIZ/GDFwH8ZI2Mfnx+/sdtkdNHzwIYNEVO3z49/8r7psjp7tPPhx2zRFZHIiKizxBZfR0REbcNkdVvERGH7JDX+xFxxgx5fRURN82Q188RtQtmyGuhNpqskFlTtBohs9boMEJmHdFthMy6Y8AImQ2E/wdPbThGjZDZaEwZIbOpmDNCZnNhg9wEIAAEgAAQAAJAAAgAASAABIAAEAACQAAIAAEgAASAABAAAkAACAABIAAEgAAQAAJAAAgAASAAtnUA46QWAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMD/ZTepeTDCiyEIAAEgAASAABAAAkAACAABIAAEgAAQAAJAAAgAASAABIAAEAACQAAIAAEgAASAABAAAkAACAABIAAEgAAQAAJAAAigsC5/Vk6/CGC7OVfW+56vCkAAAhCAAAQgAAEIQAACEIAABCAAAQhAAAIQgAAEIAABCEAAAhCAAAQgAAEIQAACEIAABCAAATgsASAABIAAEAACQAAIAAEgAATABgx/Uk63BIAAEAACQAAIAAEgAASAABAAAkAACAABIAAEgAAQAAIQgAkEgAAQAFvo1+/K6XcBbDf+M0gAAhCAAAQgAAEIQAACEIAABCAAAQhAAAIQgAAEIAABCEAAAhCAAAQgAAEIQAACEIAABCAAAQhAAAIQgAAEIAABCEAAAhCAAAQgAAEIQAACEIAABCAAAQiALSIAASAABIAAEAACQAAIAAEgAASAABAAAkAACAABIAAEgAAQAAJgBX//UU5zAthu/GOIAAQgAAEIQAACEIAABCAAAQhAAAIQgAAEIAABCEAAAhCAAAQgAAEIQAACEIAABCAAAQhAAAIQgAAEIAABCEAAAhCAAAQgAAEIQAACEIAABCAAAQhAAAIQwDY3ebWcZgSAABAAAkAACAABIAAEgAAQAAJAAAgAASAAlgYwZ4PM5mLKCJlNxagRMhuNYSNkNhwDRshsILqNkFl3dBghs45oNUJmrdFkhMyaonbBCnkt1EbcNENeNyPijBnyOhMRh8yQ16GIiNt2yOp2RET0GSKrvoiIOGaIrI5FRETlfUvkdL/y6Q0jg6bIafDZFTPHTZHT8eeXDF2zRUbXXtwydfiJNfJ5cvjlPWND5shnaMlFcwdL9simdHDpVYP9Bsmmf9ldk3sfWiSXh3uX3zbqo4HJdL9y3eyuWzbJ5NauVy8c3n/XKnnc3f/6ldNH/CSQRunISpeO+4B4Gh0rXzt/3jI5nF/l3YGq67bJ4HrVai9P1E1ap/gm61Z/e6Rhwj5FN9Hwptdnavy3eMEN17z5/aGKXhsVWW/FW5+gavf7gMIqta/lEbKWGUsV00zL2p6haxyzVRGNNa75JcJTd8xVNHdOrectyurTsyYrktnT1et8jnRPn28GC6PUt2cDL9I2Xlo0XREsXmqMjdnXeeWR/Xa2R1c6923mYeqak0O+G9ixZodO1mz+cfKqE/0j49Pz5txJ5qfHR/pPVG3hG/UV9c1tXT3n2PZ6utqa6ysCAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACib/wBZAU4orEHbcAAAAABJRU5ErkJggg=="
                />
              </defs>
            </svg>
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;


